import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { readInviteToken, clearInviteToken, acceptInviteToken } from './InvitePage';

/**
 * Redeems an invitation token stashed by /invite/:token before the person went
 * off to sign up. Mounted once in the authenticated shell, so it fires however
 * they come back — register → confirm → login, Apple Sign In, or a cold start.
 *
 * The server ALSO claims invitations by matching the signed-in user's email on
 * every /babies load, which covers anyone who joins with the address they were
 * invited on. This hook is the fallback for the case that can't: Sign in with
 * Apple hands us a private-relay address that will never match the invited one,
 * so without the token their invitation would sit pending forever.
 *
 * Silent on failure — an expired or already-claimed token is not something to
 * interrupt someone's first launch with.
 */
export default function usePendingInvite(enabled = true) {
  const queryClient = useQueryClient();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!enabled || ranRef.current) return;
    const token = readInviteToken();
    if (!token) return;
    ranRef.current = true;

    let cancelled = false;
    acceptInviteToken(token)
      .then((result) => {
        clearInviteToken();
        if (cancelled) return;
        queryClient.invalidateQueries({ queryKey: ['babies'] });
        if (result?.babyName) toast.success(`You can now help track ${result.babyName}`);
      })
      .catch((err) => {
        // Clearing on a 4xx stops us retrying a dead token on every launch.
        // A network error keeps it, and re-arms the ref, so the next launch
        // (or reconnect) can try again — otherwise accepting an invitation
        // while offline would silently lose it.
        if (err?.isNetworkError) {
          ranRef.current = false;
          return;
        }
        clearInviteToken();
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, queryClient]);
}
